import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SeriesPoint } from './SignalTimeSeries';

const CARRIER_COLORS = [
  '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
  '#0097a7', '#c62828', '#2e7d32', '#e65100', '#4527a0',
];

interface SignalCdfProps {
  title: string;
  lteSeries: SeriesPoint[];
  nrSeries: SeriesPoint[];
  carriers: string[];
  xLabel: string;
  xDomain?: [number, number];
}

interface CdfData {
  points: Array<Record<string, number | null>>;
  activeCarriers: string[];
  medians: Record<string, number>;
}

function buildCdfData(series: SeriesPoint[], carriers: string[]): CdfData {
  const carrierValues: Record<string, number[]> = {};
  carriers.forEach(c => { carrierValues[c] = []; });
  series.forEach(p => {
    if (carrierValues[p.carrier]) carrierValues[p.carrier].push(p.value);
  });

  const sorted: Record<string, number[]> = {};
  const activeCarriers: string[] = [];
  const medians: Record<string, number> = {};
  carriers.forEach(c => {
    if (carrierValues[c].length > 0) {
      sorted[c] = [...carrierValues[c]].sort((a, b) => a - b);
      activeCarriers.push(c);
      const mid = Math.floor(sorted[c].length / 2);
      medians[c] = sorted[c].length % 2 === 0
        ? (sorted[c][mid - 1] + sorted[c][mid]) / 2
        : sorted[c][mid];
    }
  });

  if (activeCarriers.length === 0) {
    return { points: [], activeCarriers: [], medians };
  }

  // All unique x values across active carriers
  const allXSet = new Set<number>();
  activeCarriers.forEach(c => sorted[c].forEach(v => allXSet.add(v)));
  const allX = Array.from(allXSet).sort((a, b) => a - b);

  // Sweep with per-carrier index pointer to compute count <= x in O(N+M)
  const carrierIdx: Record<string, number> = {};
  activeCarriers.forEach(c => { carrierIdx[c] = 0; });

  const points = allX.map(x => {
    const row: Record<string, number | null> = { x };
    activeCarriers.forEach(c => {
      const arr = sorted[c];
      while (carrierIdx[c] < arr.length && arr[carrierIdx[c]] <= x) {
        carrierIdx[c]++;
      }
      row[c] = carrierIdx[c] / arr.length;
    });
    return row;
  });

  return { points, activeCarriers, medians };
}

function CdfChart({
  label,
  series,
  carriers,
  xLabel,
  xDomain,
}: {
  label: string;
  series: SeriesPoint[];
  carriers: string[];
  xLabel: string;
  xDomain?: [number, number];
}) {
  const { points, activeCarriers, medians } = useMemo(
    () => buildCdfData(series, carriers),
    [series, carriers],
  );

  if (points.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" mb={1}>{label}</Typography>
        <Typography variant="body2" color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" mb={1}>{label}</Typography>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={points} margin={{ top: 5, right: 30, left: 30, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            type="number"
            domain={xDomain ?? ['auto', 'auto']}
            label={{ value: xLabel, position: 'insideBottom', offset: -10 }}
            tickFormatter={(v: number) => v.toFixed(0)}
          />
          <YAxis
            type="number"
            domain={[0, 1]}
            label={{ value: 'CDF', angle: -90, position: 'insideLeft' }}
            tickFormatter={(v: number) => v.toFixed(1)}
          />
          <Tooltip
            labelFormatter={(label: unknown) => `${Number(label).toFixed(1)} ${xLabel}`}
            formatter={(value: unknown) => (value === null ? '—' : Number(value).toFixed(3))}
          />
          <Legend verticalAlign="top" />
          <ReferenceLine y={0.5} stroke="#888" strokeDasharray="4 4" label={{ value: 'Median', position: 'right', fill: '#666', fontSize: 11 }} />
          {activeCarriers.map((carrier, i) => (
            <Line
              key={carrier}
              type="stepAfter"
              dataKey={carrier}
              stroke={CARRIER_COLORS[i % CARRIER_COLORS.length]}
              dot={false}
              connectNulls
              isAnimationActive={false}
              name={`${carrier} (median ${medians[carrier].toFixed(1)})`}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default function SignalCdf({
  title,
  lteSeries,
  nrSeries,
  carriers,
  xLabel,
  xDomain,
}: SignalCdfProps) {
  return (
    <Box>
      <Typography variant="h6" mb={2}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        <CdfChart
          label="LTE 4G"
          series={lteSeries}
          carriers={carriers}
          xLabel={xLabel}
          xDomain={xDomain}
        />
        <CdfChart
          label="NR 5G"
          series={nrSeries}
          carriers={carriers}
          xLabel={xLabel}
          xDomain={xDomain}
        />
      </Box>
    </Box>
  );
}
